/**
 * Touren aufzeichnen: Spaziergang, Wanderung, Velofahrt.
 *
 * Der Browser liefert über `watchPosition` eine Folge von Messungen - roh und
 * verrauscht. Was hier passiert, ist das Nötige, damit aus Messungen eine
 * Spur wird:
 *
 *  - Messungen mit schlechter Genauigkeit fliegen raus. Unter Bäumen und
 *    zwischen Häusern meldet das Gerät gern 80 Meter Unsicherheit; solche
 *    Punkte springen quer über die Karte und verlängern die Strecke.
 *  - Wer steht, bewegt sich nicht. Ohne Mindestabstand wächst die Strecke
 *    auch auf der Parkbank, weil die Messung um ein paar Meter wandert.
 *  - Höhenmeter zählen erst ab einer Schwelle. Die Höhe aus GPS schwankt
 *    stärker als die Lage; ungefiltert kommen auf flacher Strecke hundert
 *    Höhenmeter zusammen.
 *
 * Gespeichert wird im Browser, ausgegeben als GPX - das liest QGIS direkt,
 * und jedes andere Programm auch.
 */
import { distanz } from './navigation.js';

/** Schlechter gemessene Punkte verwerfen. */
const MAX_UNSICHERHEIT = 50;    // Meter
/** Darunter gilt die Bewegung als Rauschen. */
const MIN_ABSTAND = 5;          // Meter
/** Erst ab diesem Unterschied zählt ein Auf- oder Abstieg. */
const HOEHEN_SCHWELLE = 5;      // Meter
/** Schneller als das ist zu Fuss und per Velo kein echter Messwert. */
const MAX_TEMPO = 40;           // m/s, rund 144 km/h

export const ARTEN = [
  { id: 'wandern', label: 'Wanderung', gpx: 'hiking' },
  { id: 'spazieren', label: 'Spaziergang', gpx: 'walking' },
  { id: 'velo', label: 'Velofahrt', gpx: 'cycling' },
  { id: 'andere', label: 'Anderes', gpx: 'other' }
];

/* ------------------------------------------------------------- Rechnen */

/**
 * Strecke, Dauer, Höhenmeter und Tempo einer Punktfolge.
 *
 * Die Dauer ist die Zeit zwischen erstem und letztem Punkt abzüglich der
 * Pausen - und Pausen sind hier die Lücken, in denen gar nichts gemessen
 * wurde, etwa weil die Aufzeichnung angehalten war.
 */
export function statistik(punkte = []) {
  const leer = { punkte: 0, meter: 0, sekunden: 0, aufstieg: 0, abstieg: 0, tempoKmh: 0, hoehe: null };
  if (punkte.length < 2) return { ...leer, punkte: punkte.length };

  let meter = 0;
  let sekunden = 0;
  let aufstieg = 0;
  let abstieg = 0;
  let bezug = Number.isFinite(punkte[0].ele) ? punkte[0].ele : null;
  let tiefste = bezug;
  let hoechste = bezug;

  for (let i = 1; i < punkte.length; i++) {
    const a = punkte[i - 1];
    const b = punkte[i];
    meter += distanz(a, b);
    const dt = (b.t - a.t) / 1000;
    // Eine Lücke von über zwei Minuten war eine Pause, keine Bewegung.
    if (dt > 0 && dt < 120) sekunden += dt;

    if (Number.isFinite(b.ele)) {
      if (bezug === null) { bezug = b.ele; tiefste = b.ele; hoechste = b.ele; }
      else {
        const d = b.ele - bezug;
        if (Math.abs(d) >= HOEHEN_SCHWELLE) {
          if (d > 0) aufstieg += d; else abstieg -= d;
          bezug = b.ele;
        }
        tiefste = Math.min(tiefste, b.ele);
        hoechste = Math.max(hoechste, b.ele);
      }
    }
  }

  return {
    punkte: punkte.length,
    meter,
    sekunden,
    aufstieg: Math.round(aufstieg),
    abstieg: Math.round(abstieg),
    tempoKmh: sekunden > 0 ? (meter / sekunden) * 3.6 : 0,
    hoehe: bezug === null ? null : { tiefste: Math.round(tiefste), hoechste: Math.round(hoechste) }
  };
}

/* --------------------------------------------------------- Aufzeichnen */

export function createRecorder({ onUpdate, onFehler } = {}) {
  let punkte = [];
  let watchId = null;
  let pausiert = false;
  let wakeLock = null;
  let begonnen = null;
  let verworfen = 0;

  const zustand = () => ({
    laeuft: watchId !== null,
    pausiert,
    begonnen,
    verworfen,
    punkte,
    stats: statistik(punkte)
  });

  const melden = () => onUpdate?.(zustand());

  /**
   * Der Bildschirm darf nicht ausgehen.
   *
   * Nicht Bequemlichkeit: sperrt sich das Telefon, hält der Browser das
   * Skript an, und die Aufzeichnung hat ein Loch. Das Wake Lock gibt es
   * nicht überall und es fällt beim Wegschalten weg - deshalb wird es beim
   * Zurückkommen neu geholt.
   */
  async function bildschirmWach(an) {
    try {
      if (an && 'wakeLock' in navigator) wakeLock = await navigator.wakeLock.request('screen');
      else { await wakeLock?.release(); wakeLock = null; }
    } catch { /* ohne geht es auch, nur schaltet sich der Bildschirm ab */ }
  }

  function sichtbarkeit() {
    if (document.visibilityState === 'visible' && watchId !== null && !pausiert) bildschirmWach(true);
  }

  function messung(pos) {
    if (pausiert) return;
    const { latitude: lat, longitude: lng, altitude, accuracy, speed } = pos.coords;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    if (Number.isFinite(accuracy) && accuracy > MAX_UNSICHERHEIT) { verworfen++; melden(); return; }

    const punkt = {
      lat,
      lng,
      ele: Number.isFinite(altitude) ? altitude : null,
      t: pos.timestamp || Date.now(),
      acc: Number.isFinite(accuracy) ? Math.round(accuracy) : null
    };

    const letzter = punkte[punkte.length - 1];
    if (letzter) {
      const d = distanz(letzter, punkt);
      const dt = (punkt.t - letzter.t) / 1000;
      // Ein Sprung, der schneller wäre als jedes Velo, ist ein Messfehler.
      if (dt > 0 && d / dt > MAX_TEMPO) { verworfen++; melden(); return; }
      if (d < MIN_ABSTAND) {
        // Stehen bleiben heisst nicht, dass die Zeit nicht läuft: der letzte
        // Punkt bekommt die neue Zeit, die Strecke waechst nicht.
        letzter.t = punkt.t;
        melden();
        return;
      }
    }
    punkte.push(punkt);
    melden();
  }

  function start() {
    if (watchId !== null || !navigator.geolocation) return false;
    punkte = [];
    verworfen = 0;
    pausiert = false;
    begonnen = Date.now();
    bildschirmWach(true);
    document.addEventListener('visibilitychange', sichtbarkeit);
    watchId = navigator.geolocation.watchPosition(
      messung,
      // `stop` gibt die Punkte heraus - sie gehen an den Aufrufer, nicht verloren.
      (e) => { if (e.code === e.PERMISSION_DENIED) { const fertig = stop(); onFehler?.('Ortung ist nicht erlaubt.', fertig); } },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 }
    );
    melden();
    return true;
  }

  function pause(an) {
    pausiert = Boolean(an);
    bildschirmWach(!pausiert);
    melden();
  }

  /** Beenden und die Punkte herausgeben - speichern macht der Aufrufer. */
  function stop() {
    if (watchId !== null) navigator.geolocation.clearWatch(watchId);
    watchId = null;
    pausiert = false;
    document.removeEventListener('visibilitychange', sichtbarkeit);
    bildschirmWach(false);
    const fertig = punkte;
    const start = begonnen;
    punkte = [];
    begonnen = null;
    melden();
    return { punkte: fertig, begonnen: start, stats: statistik(fertig) };
  }

  return { start, pause, stop, zustand, laeuft: () => watchId !== null };
}

/* ------------------------------------------------------------ Ausgabe */

const xmlEscape = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&apos;');

const zeit = (t) => new Date(t).toISOString();

/**
 * GPX 1.1 - das Austauschformat für Spuren.
 *
 * QGIS öffnet die Datei ohne Umweg, ebenso Garmin, Komoot und alles Übrige.
 * Höhe und Zeitstempel gehören mit hinein: ohne sie ist die Spur nur eine
 * Linie, mit ihnen ein Verlauf.
 */
export function alsGpx(tour) {
  const art = ARTEN.find((a) => a.id === tour.art)?.gpx || 'other';
  const punkte = tour.punkte || [];
  const zeilen = punkte.map((p) => {
    const teile = [`    <trkpt lat="${p.lat.toFixed(7)}" lon="${p.lng.toFixed(7)}">`];
    if (Number.isFinite(p.ele)) teile.push(`      <ele>${p.ele.toFixed(1)}</ele>`);
    if (p.t) teile.push(`      <time>${zeit(p.t)}</time>`);
    teile.push('    </trkpt>');
    return teile.join('\n');
  });
  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="maps@kgva.ch" xmlns="http://www.topografix.com/GPX/1/1"
     xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
     xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">
  <metadata>
    <name>${xmlEscape(tour.name)}</name>
    ${tour.begonnen ? `<time>${zeit(tour.begonnen)}</time>` : ''}
  </metadata>
  <trk>
    <name>${xmlEscape(tour.name)}</name>
    <type>${art}</type>
    <trkseg>
${zeilen.join('\n')}
    </trkseg>
  </trk>
</gpx>
`;
}

/** GeoJSON - für alles, was lieber damit umgeht als mit GPX. */
export function alsGeoJson(tour) {
  const s = tour.stats || statistik(tour.punkte);
  return JSON.stringify({
    type: 'Feature',
    geometry: {
      type: 'LineString',
      // Höhe als dritte Koordinate, so sieht GeoJSON das vor.
      coordinates: (tour.punkte || []).map((p) =>
        (Number.isFinite(p.ele) ? [p.lng, p.lat, p.ele] : [p.lng, p.lat]))
    },
    properties: {
      name: tour.name,
      art: tour.art,
      begonnen: tour.begonnen ? zeit(tour.begonnen) : null,
      meter: Math.round(s.meter),
      sekunden: Math.round(s.sekunden),
      aufstieg: s.aufstieg,
      abstieg: s.abstieg
    }
  }, null, 2);
}

/**
 * GPX einlesen - Spuren von anderen Geräten.
 *
 * Genommen wird, was Punkte trägt: `trkpt` in Spuren, sonst `rtept` einer
 * Route, sonst einzelne `wpt`. Mehrere Segmente werden aneinandergehängt;
 * die Lücke dazwischen ist in der Statistik ohnehin eine Pause.
 */
export function ausGpx(text, dateiname = '') {
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  if (doc.querySelector('parsererror')) throw new Error('Die Datei ist kein lesbares GPX.');

  let knoten = [...doc.getElementsByTagName('trkpt')];
  if (!knoten.length) knoten = [...doc.getElementsByTagName('rtept')];
  if (!knoten.length) knoten = [...doc.getElementsByTagName('wpt')];
  if (!knoten.length) throw new Error('In der Datei stehen keine Punkte.');

  const punkte = [];
  for (const k of knoten) {
    const lat = Number(k.getAttribute('lat'));
    const lng = Number(k.getAttribute('lon'));
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    const ele = Number(k.getElementsByTagName('ele')[0]?.textContent);
    const t = Date.parse(k.getElementsByTagName('time')[0]?.textContent || '');
    punkte.push({
      lat,
      lng,
      ele: Number.isFinite(ele) ? ele : null,
      // Ohne Zeitstempel bleibt die Dauer leer - erfinden waere schlimmer.
      t: Number.isFinite(t) ? t : null,
      acc: null
    });
  }
  if (punkte.length < 2) throw new Error('In der Datei stehen keine Punkte.');

  const name = doc.querySelector('trk > name, metadata > name, rte > name')?.textContent?.trim()
    || dateiname.replace(/\.gpx$/i, '')
    || 'Eingelesene Spur';
  const typ = doc.querySelector('trk > type')?.textContent?.trim().toLowerCase();
  const art = ARTEN.find((a) => a.gpx === typ)?.id || 'andere';

  // Fuer die Statistik braucht es Zeiten; fehlen sie, zaehlt nur die Strecke.
  const mitZeit = punkte.filter((p) => p.t !== null);
  return {
    name,
    art,
    begonnen: mitZeit.length ? mitZeit[0].t : null,
    punkte,
    stats: statistik(mitZeit.length === punkte.length ? punkte : punkte.map((p) => ({ ...p, t: p.t ?? 0 })))
  };
}

/* ------------------------------------------------------------ Anzeige */

export function tourName(art, zeitpunkt = Date.now()) {
  const label = ARTEN.find((a) => a.id === art)?.label || 'Tour';
  const d = new Date(zeitpunkt);
  return `${label} ${d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
}

export function coords(punkte = []) {
  return punkte.map((p) => [p.lng, p.lat]);
}
